<script setup lang="ts">
const props = defineProps({
  number: {
    type: Number,
    required: false,
    default: undefined,
  },
  displayNumber: {
    type: [Number, String],
    required: false,
    default: undefined,
  },
  level: {
    type: Number,
    required: false,
    default: undefined,
  },
});
const isMobile = useMediaQuery("(max-width: 767px)");
const numberTooltipOpen = ref(false);
const levelTooltipOpen = ref(false);
const mobileTooltipClass =
  "card-detail-meta__tooltip fixed w-max max-w-[calc(100vw-2rem)] p-2 bg-neutral-200 flex flex-col justify-center items-center flex-nowrap rounded-sm z-50";
const mobileTooltipArrowClass = "top-2 -translate-x-1/2";
const numberContainer = ref<HTMLElement | null>(null);
const levelContainer = ref<HTMLElement | null>(null);
const numberTooltipRef = ref<{ tooltipRef: HTMLElement | null } | null>(null);
const levelTooltipRef = ref<{ tooltipRef: HTMLElement | null } | null>(null);
const numberTooltipStyle = ref<Record<string, string>>({});
const levelTooltipStyle = ref<Record<string, string>>({});
const numberArrowStyle = ref<Record<string, string>>({});
const levelArrowStyle = ref<Record<string, string>>({});

const handleDocumentClick = (event: MouseEvent) => {
  if (!isMobile.value) {
    return;
  }
  const target = event.target as HTMLElement | null;
  if (!target) {
    return;
  }
  if (target.closest(".card-detail-meta__tooltip")) {
    return;
  }
  if (numberContainer.value?.contains(target)) {
    return;
  }
  if (levelContainer.value?.contains(target)) {
    return;
  }
  numberTooltipOpen.value = false;
  levelTooltipOpen.value = false;
};

const toggleNumberTooltip = () => {
  if (!isMobile.value) {
    return;
  }
  numberTooltipOpen.value = !numberTooltipOpen.value;
  if (numberTooltipOpen.value) {
    levelTooltipOpen.value = false;
  }
};

const toggleLevelTooltip = () => {
  if (!isMobile.value) {
    return;
  }
  levelTooltipOpen.value = !levelTooltipOpen.value;
  if (levelTooltipOpen.value) {
    numberTooltipOpen.value = false;
  }
};
onMounted(() => {
  document.addEventListener("click", handleDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick);
});

const updateMobileTooltip = (
  trigger: HTMLElement | null,
  tooltip: HTMLElement | null,
  tooltipStyle: Ref<Record<string, string>>,
  arrowStyle: Ref<Record<string, string>>
) => {
  if (!trigger || !tooltip) {
    return;
  }
  const triggerRect = trigger.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const viewportPadding = 16;
  const maxTooltipWidth = viewportWidth - viewportPadding * 2;
  const tooltipWidth = Math.min(tooltipRect.width, maxTooltipWidth);
  const left = Math.max(
    viewportPadding,
    (viewportWidth - tooltipWidth) / 2
  );
  const preferredTop = triggerRect.top - tooltipRect.height - 12;
  const top =
    preferredTop < viewportPadding
      ? Math.min(triggerRect.bottom + 12, viewportHeight - viewportPadding)
      : preferredTop;
  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  const arrowLeft = Math.min(
    tooltipWidth - viewportPadding,
    Math.max(viewportPadding, triggerCenter - left)
  );

  tooltipStyle.value = {
    left: `${left}px`,
    top: `${top}px`,
  };
  arrowStyle.value = {
    left: `${arrowLeft}px`,
  };
};

const scheduleMobilePosition = () => {
  if (!isMobile.value) {
    return;
  }
  requestAnimationFrame(() => {
    if (numberTooltipOpen.value) {
      updateMobileTooltip(
        numberContainer.value,
        numberTooltipRef.value?.tooltipRef ?? null,
        numberTooltipStyle,
        numberArrowStyle
      );
    }
    if (levelTooltipOpen.value) {
      updateMobileTooltip(
        levelContainer.value,
        levelTooltipRef.value?.tooltipRef ?? null,
        levelTooltipStyle,
        levelArrowStyle
      );
    }
  });
};

watch(numberTooltipOpen, (isOpen) => {
  if (!isMobile.value || !isOpen) {
    return;
  }
  nextTick(() => scheduleMobilePosition());
});

watch(levelTooltipOpen, (isOpen) => {
  if (!isMobile.value || !isOpen) {
    return;
  }
  nextTick(() => scheduleMobilePosition());
});

onMounted(() => {
  window.addEventListener("resize", scheduleMobilePosition);
  window.addEventListener("scroll", scheduleMobilePosition, true);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", scheduleMobilePosition);
  window.removeEventListener("scroll", scheduleMobilePosition, true);
});

const formattedNumber = computed(() => props.displayNumber ?? props.number);
</script>

<template>
  <div class="flex flex-wrap items-center gap-4 text-xs font-semibold">
    <div
      v-if="number !== undefined"
      ref="numberContainer"
      class="relative flex gap-2"
      :class="isMobile ? 'flex-col items-start gap-1 cursor-pointer' : 'items-center'"
      :role="isMobile ? 'button' : undefined"
      :aria-expanded="isMobile ? numberTooltipOpen : undefined"
      :tabindex="isMobile ? 0 : undefined"
      @click="toggleNumberTooltip"
      @keydown.enter.prevent="toggleNumberTooltip">
      <template v-if="isMobile">
        <div class="flex items-center gap-1">
          <span class="text-[10px] text-neutral-400 leading-none">
            {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
          </span>
          <span class="text-neutral-200">{{ formattedNumber }}</span>
        </div>
        <CommonTooltip
          position="custom"
          :is-visible="numberTooltipOpen"
          :custom-class="mobileTooltipClass"
          :custom-arrow-class="mobileTooltipArrowClass"
          :custom-style="numberTooltipStyle"
          :custom-arrow-style="numberArrowStyle"
          :disable-shift="true"
          ref="numberTooltipRef">
          <template #tooltip-text>
            <div class="max-w-xs sm:max-w-none whitespace-normal sm:whitespace-nowrap text-xs text-neutral-800 leading-relaxed">
              <p class="font-semibold mb-1">
                {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
              </p>
              <p>
                {{ $t("main.cardDetail.tooltip.mintNumber") }}
              </p>
            </div>
          </template>
        </CommonTooltip>
      </template>
      <template v-else>
        <span class="text-neutral-400">
          {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
        </span>
        <span class="peer text-neutral-200 cursor-help">
          #{{ formattedNumber }}
        </span>
        <CommonTooltip position="top">
          <template #tooltip-text>
            <div class="max-w-xs sm:max-w-none whitespace-normal sm:whitespace-nowrap text-xs text-neutral-800 leading-relaxed">
              <p class="font-semibold mb-1">
                {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
              </p>
              <p>
                {{ $t("main.cardDetail.tooltip.mintNumber") }}
              </p>
            </div>
          </template>
        </CommonTooltip>
      </template>
    </div>
    <div
      v-if="level !== undefined && level > 0"
      ref="levelContainer"
      class="relative flex gap-2"
      :class="isMobile ? 'flex-col items-start gap-1 cursor-pointer' : 'items-center'"
      :role="isMobile ? 'button' : undefined"
      :aria-expanded="isMobile ? levelTooltipOpen : undefined"
      :tabindex="isMobile ? 0 : undefined"
      @click="toggleLevelTooltip"
      @keydown.enter.prevent="toggleLevelTooltip">
      <template v-if="isMobile">
        <div class="flex items-center gap-1">
          <span class="text-[10px] text-neutral-400 leading-none">
            {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
          </span>
          <span class="text-neutral-200">{{ level }}</span>
        </div>
        <CommonTooltip
          position="custom"
          :is-visible="levelTooltipOpen"
          :custom-class="mobileTooltipClass"
          :custom-arrow-class="mobileTooltipArrowClass"
          :custom-style="levelTooltipStyle"
          :custom-arrow-style="levelArrowStyle"
          :disable-shift="true"
          ref="levelTooltipRef">
          <template #tooltip-text>
            <div class="max-w-xs sm:max-w-none whitespace-normal sm:whitespace-nowrap text-xs text-neutral-800 leading-relaxed">
              <p class="font-semibold mb-1">
                {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
              </p>
              <p>
                {{ $t("main.cardDetail.tooltip.itemValue") }}
              </p>
            </div>
          </template>
        </CommonTooltip>
      </template>
      <template v-else>
        <span class="text-neutral-400">
          {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
        </span>
        <span class="peer text-neutral-200 cursor-help">{{ level }}</span>
        <CommonTooltip position="top">
          <template #tooltip-text>
            <div class="max-w-xs sm:max-w-none whitespace-normal sm:whitespace-nowrap text-xs text-neutral-800 leading-relaxed">
              <p class="font-semibold mb-1">
                {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
              </p>
              <p>
                {{ $t("main.cardDetail.tooltip.itemValue") }}
              </p>
            </div>
          </template>
        </CommonTooltip>
      </template>
    </div>
  </div>
</template>
